import api from "../../services/api";
import logger from "../../utils/logger";
import { Eventbus } from "../../utils";
import { events } from "../../utils/constants";
import mergeUpdatedCollectionData from "../../utils/helpers/mergeCollection";

const CentreBankingService = (function () {
    let centreTransactions = [];
    let bankTransactions = [];
    let transactionTypes = [];
    let isLoading = false;
    let pendingLoad = null;
    let pendingLoadKey = null;
    let loadedCentreKey = null;
    let activeLoadToken = null;
    let currentCentreId = null;
    let signalRListenersRegistered = false;

    function getData() {
        return {
            centreTransactions: [...centreTransactions],
            bankTransactions: [...bankTransactions]
        };
    }

    function getCentreTransactions() {
        return [...centreTransactions];
    }

    function getBankTransactions() {
        return [...bankTransactions];
    }

    function getCentreIds() {
        return Array.isArray(currentCentreId) ? currentCentreId : [currentCentreId];
    }

    function getCentreRequestKey() {
        return JSON.stringify(getCentreIds().filter(id => id !== null && id !== undefined));
    }

    async function loadData(forceRefresh = false, centreId = null) {
        // If centreId is provided, update the current centre
        if (centreId !== null && centreId !== undefined) {
            // Handle both single ID and array of IDs
            if (Array.isArray(centreId)) {
                currentCentreId = centreId.map(id => typeof id === 'number' ? id : parseInt(id, 10)).filter(id => !isNaN(id));
                if (currentCentreId.length === 0) {
                    logger.error('Invalid centreId array provided:', centreId);
                    currentCentreId = null;
                }
            } else {
                const parsedId = typeof centreId === 'number' ? centreId : parseInt(centreId, 10);
                if (isNaN(parsedId)) {
                    logger.error('Invalid centreId provided:', centreId);
                    currentCentreId = null;
                } else {
                    currentCentreId = parsedId;
                }
            }
        }

        // If no centre is selected, return early
        if (!currentCentreId || (Array.isArray(currentCentreId) && currentCentreId.length === 0)) {
            logger.debug('No valid centre selected, skipping load');
            return { centreTransactions: [], bankTransactions: [] };
        }

        const requestKey = getCentreRequestKey();

        // Reuse identical in-flight centre loads so callers don't race against an empty cache.
        if (isLoading && pendingLoad && pendingLoadKey === requestKey && !forceRefresh) {
            return pendingLoad.then((result) => ({
                centreTransactions: [...result.centreTransactions],
                bankTransactions: [...result.bankTransactions]
            }));
        }

        // If data exists and we're not forcing a refresh, return cached data
        if (centreTransactions.length > 0 && loadedCentreKey === requestKey && !forceRefresh) {
            isLoading = false;
            Eventbus.emit(events.CENTRE_TRANSACTIONS_LOADED, [...centreTransactions]);
            Eventbus.emit(events.BANK_TRANSACTIONS_LOADED, [...bankTransactions]);
            return { centreTransactions: [...centreTransactions], bankTransactions: [...bankTransactions] };
        }

        isLoading = true;
        pendingLoadKey = requestKey;
        const loadToken = {};
        activeLoadToken = loadToken;

        // Register SignalR listeners if not already registered
        registerSignalRListeners();

        pendingLoad = (async () => {
            try {
                // Load transaction types if not already loaded so helpers always work.
                if (transactionTypes.length === 0) {
                    try {
                        const types = await api.getTransactionTypes();
                        if (types && !types.code) {
                            transactionTypes = types;
                        }
                    } catch (error) {
                        logger.error('Error loading transaction types:', error);
                    }
                }

                // Determine if we have multiple centre IDs
                const centreIds = getCentreIds();

                // Fetch data for all centre IDs in parallel
                const allCentreRequests = centreIds.map(id => api.getCentreTransactions(id));
                const allBankRequests = centreIds.map(id => api.getBankTransactions(id));

                const [centreResponses, bankResponses] = await Promise.all([
                    Promise.all(allCentreRequests),
                    Promise.all(allBankRequests)
                ]);

                if (activeLoadToken !== loadToken) {
                    return { centreTransactions: [...centreTransactions], bankTransactions: [...bankTransactions] };
                }

                // Check for cancelled requests
                if (centreResponses.some(r => r?.code === 'ERR_CANCELED') || bankResponses.some(r => r?.code === 'ERR_CANCELED')) {
                    return { centreTransactions: [...centreTransactions], bankTransactions: [...bankTransactions] };
                }

                // Merge all responses into single collections
                centreTransactions = centreResponses.flat().filter(item => item) || [];
                bankTransactions = bankResponses.flat().filter(item => item) || [];
                loadedCentreKey = requestKey;

                logger.debug(`Loaded ${centreTransactions.length} centre transactions and ${bankTransactions.length} bank transactions from ${centreIds.length} centre(s)`);

                Eventbus.emit(events.CENTRE_TRANSACTIONS_LOADED, [...centreTransactions]);
                Eventbus.emit(events.BANK_TRANSACTIONS_LOADED, [...bankTransactions]);

                return { centreTransactions: [...centreTransactions], bankTransactions: [...bankTransactions] };
            } catch (error) {
                if (activeLoadToken === loadToken) {
                    logger.error('Error loading centre banking data:', error);
                }
                return { centreTransactions: [], bankTransactions: [] };
            } finally {
                if (activeLoadToken === loadToken) {
                    isLoading = false;
                    pendingLoad = null;
                    pendingLoadKey = null;
                    activeLoadToken = null;
                }
            }
        })();

        return pendingLoad;
    }

    async function upsertTransaction(transactionData) {
        try {
            const response = await api.upsertCentreTransaction(transactionData);

            if (response?.code === 'ERR_CANCELED') {
                return null;
            }

            // SignalR will handle the data updates automatically
            return response;
        } catch (error) {
            logger.error('Error upserting centre transaction:', error);
            throw error;
        }
    }

    async function matchTransactionToSplit(centreTransactionId, bankTransactionId, transactionSplitId) {
        try {
            const response = await api.matchTransactionToSplit(centreTransactionId, bankTransactionId, transactionSplitId);
            if (response?.code === 'ERR_CANCELED') {
                return null;
            }
            // SignalR will handle the data updates automatically
            return response;
        } catch (error) {
            logger.error('Error matching transaction to split:', error);
            throw error;
        }
    }

    async function deleteCentreTransaction(transactionId) {
        try {
            const response = await api.deleteCentreTransaction(transactionId);

            if (response?.code === 'ERR_CANCELED') {
                return null;
            }

            // SignalR will handle the data updates automatically
            return response;
        } catch (error) {
            logger.error('Error deleting centre transaction:', error);
            throw error;
        }
    }

    // SignalR event handlers
    function handleCentreTransactionUpdate(updatedTransaction) {
        try {
            logger.debug('Processing centre transaction update:', updatedTransaction, 'current centre ID:', currentCentreId);

            // Only process updates for the current centre
            if (!currentCentreId || !currentCentreId.includes(updatedTransaction.centreId)) {
                return;
            }

            centreTransactions = mergeUpdatedCollectionData(
                [updatedTransaction],
                centreTransactions,
                {
                    idField: 'id',
                    event: events.CENTRE_TRANSACTIONS_LOADED,
                    filter: (item) => item.transactionSplitId // Filter out transactions that have been split
                }
            );
        } catch (error) {
            logger.error('Error processing centre transaction update:', error);
        }
    }

    function handleCentreTransactionDelete(transactionId) {
        try {
            logger.debug('Processing centre transaction delete:', transactionId);

            const existingTransaction = centreTransactions.find(t => t.id === transactionId);
            if (!existingTransaction) {
                return;
            }

            centreTransactions = mergeUpdatedCollectionData(
                [{ id: transactionId, _isDeleted: true }],
                centreTransactions,
                {
                    idField: 'id',
                    hardDelete: true,
                    event: events.CENTRE_TRANSACTIONS_LOADED
                }
            );
        } catch (error) {
            logger.error('Error processing centre transaction delete:', error);
        }
    }

    function handleSplitTransactionUpdate(updatedSplit) {
        try {
            logger.debug('Processing transaction split update:', updatedSplit);

            // Find the bank transaction that this split belongs to
            const bankTransactionIndex = bankTransactions.findIndex(bt => bt.bankTransaction.id === updatedSplit.bankTransactionId);
            
            if (bankTransactionIndex === -1) {
                logger.warn('Bank transaction not found for split update:', updatedSplit);
                return;
            }

            const bankTransaction = bankTransactions[bankTransactionIndex];
            
            // Since we only load bank transactions for the current centre, we can assume
            // any split updates for existing bank transactions belong to the current centre
            if (!currentCentreId) {
                logger.debug('No current centre selected, skipping split update');
                return;
            }

            // Find the split in the transactionSplits array
            const splitIndex = bankTransaction.transactionSplits.findIndex(split => split.id === updatedSplit.id);
            
            if (splitIndex !== -1) {
                // Check if the incoming update is newer (using updatedAt or updateAt)
                const existingSplit = bankTransaction.transactionSplits[splitIndex];
                const existingTimestamp = existingSplit.updatedAt || existingSplit.updateAt || 0;
                const newTimestamp = updatedSplit.updatedAt || updatedSplit.updateAt || 0;
                
                if (newTimestamp <= existingTimestamp) {
                    logger.debug('Split update is not newer than existing, skipping:', {
                        existingTimestamp,
                        newTimestamp,
                        splitId: updatedSplit.id
                    });
                    return; // Don't update if not newer
                }
                
                // Update existing split
                bankTransaction.transactionSplits[splitIndex] = { ...existingSplit, ...updatedSplit };
                logger.debug('Updated existing split:', updatedSplit.id);
            } else {
                // Add new split
                bankTransaction.transactionSplits.push(updatedSplit);
                logger.debug('Added new split:', updatedSplit.id);
            }

            // Create a new array reference to trigger React updates
            bankTransactions = [...bankTransactions];
            
            // Emit event to notify listeners
            Eventbus.emit(events.BANK_TRANSACTIONS_LOADED, [...bankTransactions]);
        } catch (error) {
            logger.error('Error processing transaction split update:', error);
        }
    }

    function handleSplitTransactionDelete(splitId) {
        try {
            logger.debug('Processing transaction split delete:', splitId);
            // Find the bank transaction that contains this split
            const bankTransactionIndex = bankTransactions.findIndex(bt => 
                bt.transactionSplits.some(split => split.id === splitId)
            );
            if (bankTransactionIndex === -1) {
                logger.warn('Bank transaction not found for split delete:', splitId);
                return;
            }
            const bankTransaction = bankTransactions[bankTransactionIndex];
            // Remove the split from the transactionSplits array
            bankTransaction.transactionSplits = bankTransaction.transactionSplits.filter(split => split.id !== splitId);

            // Create a new array reference to trigger React updates
            bankTransactions = [...bankTransactions];
            // Emit event to notify listeners
            Eventbus.emit(events.BANK_TRANSACTIONS_LOADED, [...bankTransactions]);
        } catch (error) {
            logger.error('Error processing transaction split delete:', error);
        }
    }

    function handleMerchantFileUploaded() {
        (async () => {
            try {
                logger.debug('Merchant file uploaded, reloading bank transactions for centre:', currentCentreId);

                if (!currentCentreId) {
                    logger.debug('No current centre selected, skipping bank transactions reload');
                    return;
                }

                const bankResponse = await api.getBankTransactions(currentCentreId);

                if (bankResponse?.code === 'ERR_CANCELED') {
                    return;
                }

                bankTransactions = bankResponse || [];
                Eventbus.emit(events.BANK_TRANSACTIONS_LOADED, [...bankTransactions]);
                logger.debug('Bank transactions reloaded successfully');
            } catch (error) {
                logger.error('Error reloading bank transactions after merchant file upload:', error);
            }
        })();
    }

    function registerSignalRListeners() {
        if (signalRListenersRegistered) {
            logger.debug('SignalR listeners already registered, skipping');
            return;
        }

        try {
            // Register listeners
            Eventbus.on(events.SIGNALR.CENTRE_TRANSACTION_DATA_CHANGES, handleCentreTransactionUpdate);
            Eventbus.on(events.SIGNALR.CENTRE_TRANSACTION_DATA_DELETE, handleCentreTransactionDelete);
            Eventbus.on(events.SIGNALR.TRANSACTION_SPLIT_DATA_CHANGES, handleSplitTransactionUpdate);
            Eventbus.on(events.SIGNALR.TRANSACTION_SPLIT_DATA_CHANGES_DELETE, handleSplitTransactionDelete);
            Eventbus.on(events.SIGNALR.CENTRE_BANKING_MERCHANT_FILE_UPLOADED, handleMerchantFileUploaded);

            signalRListenersRegistered = true;
            logger.debug('SignalR centre transaction listeners registered');
        } catch (error) {
            logger.error('Error registering SignalR listeners:', error);
        }
    }

    function removeSignalRListeners() {
        if (!signalRListenersRegistered) {
            return;
        }

        try {
            Eventbus.off(handleCentreTransactionUpdate);
            Eventbus.off(handleCentreTransactionDelete);
            Eventbus.off(handleSplitTransactionUpdate);
            Eventbus.off(handleSplitTransactionDelete);
            Eventbus.off(handleMerchantFileUploaded);

            signalRListenersRegistered = false;
            logger.debug('SignalR centre transaction listeners removed');
        } catch (error) {
            logger.error('Error removing SignalR listeners:', error);
        }
    }

    function getLoadingState() {
        return isLoading;
    }

    function clear() {
        removeSignalRListeners();
        centreTransactions = [];
        bankTransactions = [];
        transactionTypes = [];
        currentCentreId = null;
        isLoading = false;
        pendingLoad = null;
        pendingLoadKey = null;
        loadedCentreKey = null;
        activeLoadToken = null;
        signalRListenersRegistered = false; // Ensure flag is reset
    }

    function init(centreId) {
        registerSignalRListeners();
        return loadData(true, centreId);
    }

    function getCurrentCentreId() {
        return currentCentreId;
    }

    function getTransactionTypes() {
        return [...transactionTypes];
    }

    // Get the payment type label ("Cash", "Eftpos") for a bank transaction code ("ATM", "DEP", "PS", etc.)
    function getPaymentTypeForCode(code) {
        const entry = transactionTypes.find(t => t.code?.toUpperCase() === code?.toUpperCase());
        if (!entry) return null;
        // Normalise: first letter uppercase, rest lowercase
        const type = entry.type;
        return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    }

    // Check whether a bank transaction code belongs to the EFTPOS type
    function isEftposCode(code) {
        const paymentType = getPaymentTypeForCode(code);
        return paymentType?.toLowerCase() === 'eftpos';
    }

    // Check whether a bank transaction code belongs to the CASH type
    function isCashCode(code) {
        const paymentType = getPaymentTypeForCode(code);
        return paymentType?.toLowerCase() === 'cash';
    }

    // Get display info for a bank transaction code { label, className, paymentType }
    function getTransactionTypeInfo(code) {
        const paymentType = getPaymentTypeForCode(code);
        if (!paymentType) return { label: code || "Other", className: "other", paymentType: null };
        return {
            label: paymentType,
            className: paymentType.toLowerCase(),
            paymentType: paymentType.toLowerCase(),
        };
    }

    function setCurrentCentreId(centreId) {
        currentCentreId = centreId;
    }

    async function unlinkTransaction(splitId) {
        try {
            const response = await api.unlinkTransactionFromSplit(currentCentreId, splitId);
            if (response?.code === 'ERR_CANCELED') {
                return null;
            }
            // SignalR will handle the data updates automatically
            return response;
        } catch (error) {
            logger.error('Error unlinking transaction from split:', error);
            // Don't re-throw the error - let the global API handler show the toast
            return null;
        }
    }

    async function deleteTransaction(transactionId) {
        try {
            const response = await api.deleteCentreTransaction(transactionId);
            if (response?.code === 'ERR_CANCELED') {
                return null;
            }
            // SignalR will handle the data updates automatically
            return response;
        } catch (error) {
            logger.error('Error deleting centre transaction:', error);
        }
    }

    function deleteTransactionSplit(transactionId) {
              return api.deleteTransactionSplit(transactionId);
    }

    function syncCentre(centreId) {
        return api.syncCentre(centreId)
            .then((response) => {
                if (response?.code === 'ERR_CANCELED') {
                    logger.warn('Centre sync cancelled:', centreId);
                    return response;
                }
                return response;
            })
            .catch((error) => {
                logger.error('Error syncing centre:', error);
                throw error;
            });
    }

    return {
        getData,
        getCentreTransactions,
        getBankTransactions,
        loadData,
        upsertTransaction,
        deleteCentreTransaction,
        getLoadingState,
        clear,
        init,
        getCurrentCentreId,
        setCurrentCentreId,
        matchTransactionToSplit,
        unlinkTransaction,
        deleteTransaction,
        deleteTransactionSplit,
        syncCentre,
        getTransactionTypes,
        getPaymentTypeForCode,
        isEftposCode,
        isCashCode,
        getTransactionTypeInfo,
    };
})();

export default CentreBankingService;
